import enTranslations from '@shopify/polaris/locales/en.json'

export default {
  ...enTranslations,
  Prisync: {
    settings: {
      label: 'Settings',
      tabs: {
        prisyncPreferences: 'Preferences',
        shopifyPreferences: 'Shopify',
        competitorPreferences: 'Competitor',
      },
      emptyState: {
        title: 'Complete the onboarding to configure your settings',
        description:
          'On this page, you can manage your app settings. To continue, please complete the setup.',
      },
      competitor: {
        monitoringStyle: {
          title: 'Competitor Monitoring Style',
          description:
            'This setting shows your current monitoring style. To change it',
        },
        googleShoppingCountry: {
          title: 'Google Shopping Country',
          description: 'Contact us to update.',
        },
        amazonCountry: {
          title: 'Amazon Country',
          description: 'Contact us to update.',
        },
        amazonChannelConfiguration: {
          title: 'Amazon Channel Configuration',
          description: 'Choose which Amazon offers to monitor.',
          buybox: {
            title: 'Amazon Buybox',
            description: 'Monitor Buybox winners.',
          },
          ownOfferings: {
            title: 'Amazon’s Own Offering',
            description: 'Monitor Amazon’s direct listings.',
          },
        },
        enableSpecificUrl: {
          title: 'Enable URL Pause on Product Card',
          description:
            'This setting lets you pause specific competitor URLs for a product, with a pause icon shown on the product card’s competitor list.',
        },
        includeExcludeCompetitor: {
          title: 'Include/Exclude Specific Competitor Domains from Tracking',
          description: '',
          includePlaceholder: 'No selection means all are included',
          excludePlaceholder: 'No selection means nothing excluded',
        },
      },
    },
    shopifyPreferences: {
      label: 'Shopify Preferences',
      learnMore:
        'You can find detailed information about Shopify settings on our',
      toastMessage: {
        successfullySaveTitle: 'Preferences Saved',
        successfullySaveHeading: 'Preferences saved!',
        successfullySaveMessage:
          'Your preferences have been successfully saved.',
        saveError: 'Something went wrong while saving your preferences.',
      },
      syncAttributesField: {
        title: 'Select product fields to keep synchronized with Prisync',
        description:
          'Selected details will stay up-to-date for products already in Prisync.',
      },
      listSyncField: {
        title: 'Sync your product list with Shopify?',
        description:
          'Imports all active products from Shopify and keeps their details up-to-date in Prisync. You can edit and freeze details anytime.',
      },
      doNotImportOOS: {
        title: 'Do not import my out-of-stock products',
        description:
          'Skip importing out-of-stock products from your Shopify store.',
      },
      addArchivedDraft: {
        title: 'Add archived and draft products to Prisync?',
        description:
          'Include unpublished products like archived or drafts in Prisync.',
      },
      triggerDeletion: {
        title:
          'Which Shopify product states should trigger deletion in Prisync?',
        description:
          'Products in the selected states will be removed from Prisync.',
        archivedLabel: 'Archived',
        draftLabel: 'Draft',
      },
      setPrices: {
        title: 'Enable automatic price updates with SmartPrice?',
        description:
          "Let Prisync's Repricing Engine instantly update your prices based on SmartPrice calculations.",
        repricingConflict: {
          title: 'Switch repricing to this market?',
          body: 'Repricing is currently active on <strong>{{panelName}}</strong>. Only one no-catalog market can have repricing enabled at a time, since they share the same product list.',
          bodySharedCatalog:
            'Repricing is currently active on <strong>{{panelName}}</strong>, which shares this catalog. Markets sharing a catalog write to the same price list, so only one of them can have repricing enabled at a time.',
          bodySwitch:
            'Switching will disable repricing on the other market and enable it here.',
          confirm: 'Switch here',
          cancel: 'Cancel',
        },
      },
      showPriceAsItIs: {
        title: 'How should Compare-at Price (CaP) be managed?',
        description:
          'Shopify displays a strikethrough price when your actual price is lower than CaP.',
        dontCAPLabel: 'Do not change CaP',
        dontCAPText:
          'Only your actual price is updated based on Dynamic Pricing rules. CaP stays the same.',
        setCAPLabel: 'Set previous price as new CaP',
        setCAPText:
          'Both your actual price and CaP are updated by Prisync Repricing Engine.',
      },
      smartPriceCalculationMode: {
        defaultOption: 'Reprice independently',
        defaultOptionDescription: 'Each variant will get its own SmartPrice.',
        minOption: 'Use minimum SmartPrice',
        minOptionDescription:
          'All variants will follow the lowest SmartPrice among them.',
        maxOption: 'Use maximum SmartPrice',
        maxOptionDescription:
          'All variants will follow the highest SmartPrice among them.',
        title: 'How should your variants be repriced',
        label: 'Smart Price Calculation Mode',
      },
      common: {
        learnMore: 'Learn more.',
        yes: 'Yes',
        no: 'No',
      },
    },
    prisyncPreferences: {
      label: 'Prisync Preferences',
      VATTypeField: {
        title: 'Display prices with or without VAT',
        description:
          'Your selection affects how we show and calculate all prices in your dashboard, including competitor prices.',
      },
      showStockOutField: {
        title:
          'Include out of stock product prices in Excel Reports & Position Calculations',
      },
      includeStockOutField: {
        title: 'Include out of stock products in SmartPrice Calculations',
      },
      currency: {
        title: 'Currency',
        description: 'Choose the currency to display all prices in.',
      },
      priceChangeThreshold: {
        title: 'Email alerts for price changes over threshold',
        description:
          'Receive email notifications only when a price change exceeds the amount you specify. ',
      },
      decimalDelimiterChar: {
        title: 'Decimal Symbol in Excel Export',
        description:
          'Choose how decimal values appear in your exported Excel reports. ',
      },
    },
    subscription: {
      label: 'Subscription',
      seeAllPlans: 'See All Plans',
      detailsLabel: 'Subscription Details',
      trialPlan: "You're on a Trial Plan",
      trialEnds: 'Your free trial ends on <s>{{date}}</s>.',
      trialEndsIn: 'Your free trial ends in <s>{{days}}</s> days.',
      trialEndPackagePrice:
        'At the end of the trial, your selected plan will automatically be activated for {{price}}.',
      trialReview: 'To review your options, click "<s>{{link}}</s>".',
      planMonthly: '{{plan}} - Monthly',
      planSince: 'You’ve been a member since <s>{{date}}</s>.',
      planRenew:
        'Your subscription renews automatically each month unless you cancel.',
      planReview: 'To manage your plan, click <s>"{{link}}"</s>.',
      emptyState: {
        title: 'Complete the setup to start your trial period.',
        description:
          'On this page you can view details like your subscription status and plan. To continue please complete the onboarding.',
      },
    },
    onboarding: {
      label: 'Onboarding',
    },
    overview: {
      label: 'Overview',
      title: 'Access Your Prisync Dashboard',
      description:
        "Get started with Prisync to track your competitors' prices and optimize your pricing strategy.",
      action: 'Add Products to Prisync',
      secondaryAction: 'Manage Pricing Rules',
      cards: {
        summary: {
          title: 'Key Metrics Summary',
          tooltip: {
            title: 'Key Metrics Summary',
            body: 'Lorem Ipsum',
          },
          elements: {
            competitorCoverage: {
              tooltip: {
                body: 'Products with competitor price data.',
              },
            },
          },
        },
        dpRules: {
          title: 'Dynamic Pricing Rules',
        },
        priceChanges: {
          title: 'Price Changes',
        },
        positioning: {
          title: 'Market Positioning',
        },
        competitorAnalysis: {
          title: 'Competitor Analysis',
        },
        oufOfStock: {
          title: 'Out of Stock',
        },
        productDetails: {
          title: 'Manage Product Details',
          description:
            'Easily manage products: export data, add, update, or delete competitor URLs, remove products, and more--all from the Product page.',
          action: 'Products',
        },
        reportDetails: {
          title: 'Reports Details',
          description:
            'Explore detailed brand and category reports with competitor analysis, index and market position insights.',
          action: 'Reports',
        },
      },
      emptyState: {
        title: 'Complete the setup to start viewing your data.',
        description:
          'You can manage your product and competitor analysis on this page. To get started please complete the onboarding.',
      },
    },
    appConnectError: {
      message:
        'Something went wrong while trying to connect you to Prisync. For further investigation please contact with the support team.',
      title: "Couldn't connect to Prisync",
      action: 'Contact support',
    },
    ERRORS: {
      default: 'An error occurred.',
      title: 'Something went wrong',
      UNAUTHORIZED: 'You are not authorized to access this page.',
      noProduct: 'Add products to start tracking your competitors.',
      bridgeUnavailableTitle: 'Open this app from Shopify admin',
      bridgeUnavailableMessage:
        "The Shopify App Bridge isn't available in this context. Please open the app from your Shopify admin to continue.",
    },
    menu: {
      home: 'Home',
      overview: 'Overview',
      markets: 'Markets',
      settings: 'Settings',
      subscription: 'Subscription',
      onboarding: 'Onboarding',
    },
    markets: {
      label: 'Markets',
      subtitle: 'Install Prisync on each of your Shopify Markets.',
      cta: {
        title: 'Shopify Markets Integration',
        description:
          'Install Prisync on each of your Shopify Markets and track competitor prices per region.',
        button: 'Install on Markets',
      },
      pool: {
        title: 'Shared product limit',
        summary: '{{used}} / {{total}} products used across all markets',
      },
      switcher: {
        selectMarket: 'Select Market',
        defaultStore: 'Default Store',
        permissionsRequired: 'Permissions required',
        permissionsHelpText: 'Go to Markets to grant access',
        // In-page banner on /markets; merchant is already on the page so
        // the switcher-side "Go to Markets" copy doesn't apply here.
        grantPermissions: 'Grant access',
        rerequestPermissions: 'Re-request permissions',
        permissionsDeniedHelpText:
          'Markets access was not granted. Re-request to enable Markets.',
        sectionTitle: 'Markets',
        manageMarkets: 'Manage Markets',
        notActivated: 'Not installed',
        clickToActivate: 'Click to install',
        switchError: 'Could not switch panel. Please try again.',
        managedByMarket: 'Managed by {{name}}',
      },
      list: {
        filters: {
          all: 'All markets',
          active: 'Active',
          draft: 'Draft',
          connected: 'Installed',
          notConnected: 'Not installed',
        },
        status: {
          active: 'Active',
          draft: 'Draft',
          connected: 'Installed',
          disconnected: 'Inactive',
          zeroCatalog: 'Zero-catalog market',
          noCatalog: 'No Catalog',
          repricingOn: 'Repricing on',
        },
        catalogCountSingular: '{{count}} catalog',
        catalogCountPlural: '{{count}} catalogs',
        trackingStyle: {
          urlBased: 'URL Based',
          channelBased: 'Channel Based',
          hybrid: 'Hybrid',
        },
        panel: {
          goToPanel: 'Open in Prisync',
          removePanel: 'Uninstall',
          setupPanel: 'Install',
          activateButton: 'Install',
          cancelButton: 'Cancel',
          connectedBadge: 'Installed',
          disconnectedBadge: 'Inactive',
          noPriceListBadge: 'No price list',
          priceListWillBeCreated:
            "This market's catalog has no price list yet. Prisync will create one to manage prices for this market.",
          managedByTooltip:
            'This catalog is already managed by the {{name}} panel.',
          managedByBadge: 'Managed by {{name}}',
          productCountSingular: '{{count}} product',
          productCountPlural: '{{count}} products',
        },
        form: {
          selectCountryPlaceholder: 'Select country',
          selectCountryLabel: 'Select country',
          panelCountryLabel: 'Country',
          panelCurrencyLabel: 'Currency',
          panelCountryRequired: 'Country is required',
          panelCurrencyLockedHint:
            "Set by this market's catalog and kept in sync with Shopify.",
          panelCurrencyStoreHint:
            "Set to your store's main currency, the same as shown for this market.",
          googleShoppingLabel: 'Google Shopping',
          amazonLabel: 'Amazon',
        },
        toast: {
          activateSuccess: 'Prisync installed on this market.',
          activateError: "Couldn't install on this market.",
          removeSuccess: 'Prisync uninstalled from this market.',
          removeError: "Couldn't uninstall from this market.",
        },
        activation: {
          marketLabel: 'Market',
          catalogLabel: 'Catalog',
          trackingStyleLabel: 'Competitor Tracking Style',
          barcodeMatchingInfo:
            'Prisync automatically matches your products with competitors using product barcodes (GTIN, EAN or UPC).',
        },
        uninstallConfirm: {
          title: 'Uninstall Prisync from this market?',
          body: 'Prisync will stop tracking this market. You can install it again later.',
        },
      },
      upgrade: {
        badge: 'Platinum Feature',
        paymentError:
          'Could not start the upgrade checkout. Please try again in a moment.',
        title: 'Unlock Shopify Markets',
        description:
          'Install Prisync on every Shopify Market — one login, complete control.',
        upgradeButton: 'Upgrade to Platinum →',
        maybeLater: 'Maybe later',
        features: {
          separatePanel: 'Per-market installation',
          independentPricing: 'Independent pricing rules',
          singleLogin: 'Single login, all markets',
          b2cB2bSupport: 'B2C & B2B catalog support',
          sharedProductLimit: 'Shared product limit pool',
        },
      },
    },
  },
}
